import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { notification } from 'antd';
import moment from 'moment';

// services
import * as SubscriptionService from '../../../../services/bb-api/subscription';
import type { Subscriber, PlanDistribution } from '../../../../services/bb-api/subscription';

export const Numbers: React.FC = () => {
  const [activeSubs, setActiveSubs] = useState<number>(0);
  const [activeSubsToday, setActiveSubsToday] = useState<number>(0);
	const [activeSubsYesterday, setActiveSubsYesterday] = useState<number>(0);
	const [planDistribution, setPlanDistribution] = useState<PlanDistribution | null>(null);
	const [latestSubscribers, setLatestSubscribers] = useState<Subscriber[]>([]);

	const getData = useCallback(() => {
		try {
			SubscriptionService.activeSubscriptions('all').then(setActiveSubs);
			SubscriptionService.activeSubscriptions('24h').then(setActiveSubsToday);
			SubscriptionService.activeSubscriptions('48h').then(setActiveSubsYesterday);

			SubscriptionService.planDistribution().then(setPlanDistribution);

			SubscriptionService.latestSubscribers().then(setLatestSubscribers);
		} catch (error) {
			const message = 'Der skete en fejl';
			notification.error({ message });
		}
	}, [])

	useEffect(() => {
		getData();
		const intervalTime = 20 * 1000 // 20 seconds
		const intervalId = setInterval(getData, intervalTime)
		return () => clearInterval(intervalId)
	}, [getData]);

	function percentage(total: number, part: number | undefined) {
		if (!total || !part) return '%'
		return (part / total * 100).toFixed(1).toString() + '%'
	}

	function firstname(fullName: string) {
		const name = fullName.substring(0, fullName.indexOf(' '))
		if (name !== '') return name
		return fullName
	}

	function userAge(birthYear: number) {
		return new Date().getFullYear() - birthYear
	}

	function planPrice(plan: string) {
		let price = plan.substring(plan.indexOf('-') + 1, plan.length)
		if (price.indexOf('-') !== -1) price = price.substring(0, price.indexOf('-'))
		return price
	}

	function planCurrency(plan: string) {
		if (plan.includes('sv')) return 'SEK'
		return 'DKK'
	}

	return (
		<StyledContainer className='numbers'>
			{/* Subscribers: Total / Today / Yesterday */}
			<div className='flex-item'>
				<div className='num-container'>
					<p className='label'>Total</p>
					<h1>{activeSubs.toLocaleString()}</h1>
				</div>
				<div className='num-container'>
					<p className='label'>I dag</p>
					<h1>{activeSubsToday.toLocaleString()}</h1>
				</div>
				<div className='num-container'>
					<p className='label'>I går</p>
					<h1>{(activeSubsYesterday - activeSubsToday).toLocaleString()}</h1>
				</div>
			</div>

			{/* Subscription plan distribution */}
			<div className='flex-item'>
				<div className='plan-container'>
					<p>
						<span className='light-grey'>Månedligt: </span>
						{planDistribution?.monthly} 
						<span className='light-grey percentage'> - {percentage(activeSubs, planDistribution?.monthly)}</span>
					</p>
					<p>
						<span className='light-grey'>Halvårligt: </span>
						{planDistribution?.halfyearly} 
						<span className='light-grey percentage'> - {percentage(activeSubs, planDistribution?.halfyearly)}</span>
					</p>
					<p>
						<span className='light-grey'>Årligt: </span>
						{planDistribution?.yearly} 
						<span className='light-grey percentage'> - {percentage(activeSubs, planDistribution?.yearly)}</span>
					</p>
				</div>
			</div>

			{/* Latest subscribers */}
			<div className='flex-item'>
				<div className='subs-container'>
					<p className='label'>Seneste abonnenter</p>
					{latestSubscribers.map((subscriber, index) => (
						<div className={`sub-item ${subscriber.state === 'cancelled' ? 'sub-cancelled' : ''}`} key={index}>
							<p className='sub-name'>{firstname(subscriber.username)}</p>
							<p className='sub-age'>{userAge(subscriber.birthYear)} <span className='light-grey'>år</span></p>
							<p className='sub-muni'>{subscriber.municipality}</p>
							<p className='sub-plan'>{planPrice(subscriber.plan)} <span className='currency'>{planCurrency(subscriber.plan)}</span></p>
							<p className='sub-created'><span className='light-grey'>{moment(subscriber.first_period_start).format('DD-MM-YYYY HH:mm')}</span></p>
						</div>
					))}
				</div>
			</div>
		</StyledContainer>
	);
};

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	color: #9ca3af;

	h1 {
		font-size: 84px;
		font-weight: 700;
		line-height: 1em;
		color: #ffffff;
		margin: 0;
	}

	p {
		margin: 0;
	}

	.label {
		font-size: 32px;
		margin: 0;
	}

	.flex-item {
		display: flex;
	}

	.num-container {
		display: flex;
		flex: 1;
		flex-direction: column;
		background-color: rgba(31, 41, 55, 1);
		height: 180px;
		margin: 1em;
		padding: 12px 48px;
		border-radius: 12px;
		box-shadow: rgba(0,0,0,0.2) 0px 2px 8px 0px;
	}

	.plan-container {
		display: flex;
		flex-direction: row;
		width: 100%;
		background-color: rgba(31, 41, 55, 1);
		margin: 1em;
		padding: 12px 48px;
		border-radius: 12px;
		box-shadow: rgba(0,0,0,0.2) 0px 2px 8px 0px;
		justify-content: space-between;

		p {
			font-size: 20px;
			color: #ffffff;
			font-weight: bold;
			min-width: 220px;
		}

		.percentage {
			font-weight: normal;
		}
	}

	.subs-container {
		display: flex;
		flex-direction: column;
		width: 100%;
		background-color: rgba(31, 41, 55, 1);
		margin: 1em;
		padding: 12px 48px;
		border-radius: 12px;
		box-shadow: rgba(0,0,0,0.2) 0px 2px 8px 0px;
	}

	.sub-item {
		display: flex;
		padding: 12px 0;
		color: #ffffff;

		p {
			font-size: 20px;
			font-weight: bold;
		}
	}

	.sub-item + .sub-item {
		border-top: 1px solid #394350;
	}

	.sub-name {
		width: 240px;
	}

	.sub-age {
		width: 125px;
	}

	.sub-muni {
		width: 250px;
	}

	.sub-plan {
		width: 180px;
	}

	.sub-cancelled {
		opacity: 0.5 !important;
		color: #9ca3af;
	}

	.currency {
		color: #9ca3af;
		padding-left: 6px;
	}

	.light-grey {
		color: #9ca3af;
	}
`;
